/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, Select, SelectItem } from '@heroui/react';
import {
  Elements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
// eslint-disable-next-line import/no-extraneous-dependencies, @typescript-eslint/ban-ts-comment
// @ts-ignore
import {
  Country,
  State,
  City,
  ICity,
  ICountry,
  IState,
} from 'country-state-city';
// eslint-disable-next-line import/no-extraneous-dependencies, @typescript-eslint/ban-ts-comment
// @ts-ignore
import postalCodes from 'postal-codes-js';
import SetupProgress from '../shared/SetupProgress';
import BackgroundSquares from '../shared/Background';
import Button from '../shared/Button';
import { ROUTES } from '../../../constants';

const stripePublishableKey =
  (import.meta as unknown as { env?: Record<string, string> }).env
    ?.REACT_APP_STRIPE_PUBLIC_KEY ||
  process.env.REACT_APP_STRIPE_PUBLIC_KEY ||
  '';

const stripePromise = loadStripe(stripePublishableKey);

const BillingForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState('');
  const [countries, setCountries] = useState<ICountry[]>([]);
  const [states, setStates] = useState<IState[]>([]);
  const [cities, setCities] = useState<ICity[]>([]);

  const [formData, setFormData] = useState({
    billingName: '',
    billingEmail: '',
    streetAddress: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    saveCard: true,
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value, type } = e.target;
    setFormData({
      ...formData,
      [name]:
        type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    });
  };

  const handleContinue = () => {
    navigate(ROUTES.SETUP_PROFILE, {
      state: {
        ...location.state,
        billingDetails: formData,
      },
    });
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setError('');

    if (!stripe || !elements) {
      setError('Payment service is not ready. Please try again in a moment.');
      return;
    }

    const cardNumberElement = elements.getElement(CardNumberElement);
    if (!cardNumberElement) {
      setError('Payment fields are not available.');
      return;
    }

    const { error: pmError } = await stripe.createPaymentMethod({
      type: 'card',
      card: cardNumberElement,
      billing_details: {
        name: formData.billingName,
        email: formData.billingEmail,
        address: {
          line1: formData.streetAddress,
          city: formData.city,
          state: formData.state,
          postal_code: formData.zipCode,
          country: formData.country,
        },
      },
    });

    if (pmError) {
      setError(pmError.message || 'Unable to save card. Please check details.');
      return;
    }

    handleContinue();
  };

  useEffect(() => {
    const allCountries = Country.getAllCountries();
    setCountries(allCountries);
    const usStates = State.getStatesOfCountry(formData.country);
    setStates(usStates);
    const defaultState = usStates.find(
      (stateItem: IState) => stateItem.isoCode === formData.state,
    );
    if (defaultState) {
      const cityList = City.getCitiesOfState(
        formData.country,
        defaultState.isoCode,
      );
      setCities(cityList);
    }
  }, []);

  const handleCountryChange = (value: string) => {
    const nextCountry = value;
    const nextStates = State.getStatesOfCountry(nextCountry);
    setFormData({
      ...formData,
      country: nextCountry,
      state: '',
      city: '',
      zipCode: '',
    });
    setStates(nextStates);
    setCities([]);
  };

  const handleStateChange = (value: string) => {
    const nextCities = value
      ? City.getCitiesOfState(formData.country, value)
      : [];
    setFormData({
      ...formData,
      state: value,
      city: '',
    });
    setCities(nextCities);
  };

  const handleZipBlur = () => {
    if (!formData.zipCode || !formData.country) return;
    const validation = postalCodes.validate(formData.country, formData.zipCode);
    if (validation !== true) {
      setError(
        typeof validation === 'string' ? validation : 'Invalid postal code.',
      );
    }
  };

  return (
    <>
      <BackgroundSquares />
      <div className="relative min-h-screen px-4 py-8 z-10">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <Card className="p-8 shadow-sm rounded-2xl">
                <div className="mb-2">
                  <span className="text-sm text-gray-500">
                    Step 2 of 4 • Billing Details
                  </span>
                </div>
                <h1 className="text-3xl font-bold text-gray-800 mb-2">
                  Add Your Billing Details
                </h1>
                <p className="text-gray-600 mb-8">
                  Enter your company&apos;s billing information to activate job
                  postings and manage payments.
                </p>

                <form className="space-y-6" onSubmit={handleSubmit}>
                  <div>
                    <div className="flex items-center gap-2 mb-4">
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M3 21H21M5 21V7L12 3L19 7V21M9 9V21M15 9V21"
                          stroke="#6B7280"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <h2 className="text-lg font-semibold text-gray-800">
                        Billing Information
                      </h2>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <label
                          htmlFor="billingName"
                          className="block text-sm font-medium text-gray-700 mb-2"
                        >
                          Billing Name / Cardholder Name
                        </label>
                        <input
                          id="billingName"
                          type="text"
                          name="billingName"
                          value={formData.billingName}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#8510DF]"
                        />
                      </div>

                      <div>
                        <label
                          htmlFor="billingEmail"
                          className="block text-sm font-medium text-gray-700 mb-2"
                        >
                          Billing Email Address
                        </label>
                        <input
                          id="billingEmail"
                          type="email"
                          name="billingEmail"
                          value={formData.billingEmail}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#8510DF]"
                        />
                      </div>

                      <div>
                        <label
                          htmlFor="streetAddress"
                          className="block text-sm font-medium text-gray-700 mb-2"
                        >
                          Street Address
                        </label>
                        <input
                          id="streetAddress"
                          type="text"
                          name="streetAddress"
                          value={formData.streetAddress}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#8510DF]"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label
                            htmlFor="country-select"
                            className="block text-sm font-medium text-gray-700 mb-2"
                          >
                            Country
                          </label>
                          <div className="w-full">
                            <Select
                              id="country-select"
                              placeholder="Select Country"
                              selectedKeys={
                                formData.country ? [formData.country] : []
                              }
                              onSelectionChange={(keys) => {
                                const selectedValue = Array.from(
                                  keys,
                                )[0] as string;
                                handleCountryChange(selectedValue || '');
                              }}
                              classNames={{
                                popoverContent: 'bg-white',
                              }}
                            >
                              {countries.map((c) => (
                                <SelectItem key={c.isoCode}>
                                  {c.name}
                                </SelectItem>
                              ))}
                            </Select>
                          </div>
                        </div>

                        <div>
                          <label
                            htmlFor="state-select"
                            className="block text-sm font-medium text-gray-700 mb-2"
                          >
                            State / Province
                          </label>
                          <div className="w-full">
                            <Select
                              id="state-select"
                              placeholder="Select State"
                              selectedKeys={
                                formData.state ? [formData.state] : []
                              }
                              onSelectionChange={(keys) => {
                                const selectedValue = Array.from(
                                  keys,
                                )[0] as string;
                                handleStateChange(selectedValue || '');
                              }}
                              classNames={{
                                popoverContent: 'bg-white',
                              }}
                            >
                              {states.map((s) => (
                                <SelectItem key={s.isoCode}>
                                  {s.name}
                                </SelectItem>
                              ))}
                            </Select>
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label
                            htmlFor="city-select"
                            className="block text-sm font-medium text-gray-700 mb-2"
                          >
                            City
                          </label>
                          <div className="w-full">
                            <Select
                              id="city-select"
                              placeholder="Select City"
                              selectedKeys={
                                formData.city ? [formData.city] : []
                              }
                              onSelectionChange={(keys) => {
                                const selectedValue = Array.from(
                                  keys,
                                )[0] as string;
                                handleChange({
                                  target: {
                                    name: 'city',
                                    value: selectedValue || '',
                                  },
                                } as React.ChangeEvent<HTMLInputElement>);
                              }}
                              classNames={{
                                popoverContent: 'bg-white',
                              }}
                            >
                              {cities.map((c) => (
                                <SelectItem
                                  key={`${c.name}-${c.latitude}-${c.longitude}`}
                                >
                                  {c.name}
                                </SelectItem>
                              ))}
                            </Select>
                          </div>
                        </div>
                        <div>
                          <label
                            htmlFor="zipCode"
                            className="block text-sm font-medium text-gray-700 mb-2"
                          >
                            Zip Code
                          </label>
                          <input
                            id="zipCode"
                            type="text"
                            name="zipCode"
                            value={formData.zipCode}
                            onChange={handleChange}
                            onBlur={handleZipBlur}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#8510DF]"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center gap-2 mb-4">
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M3 10H21M7 15H7.01M17 15H17.01M6 3V7M18 3V7M3 7H21V19C21 20.1046 20.1046 21 19 21H5C3.89543 21 3 20.1046 3 19V7Z"
                          stroke="#6B7280"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <h2 className="text-lg font-semibold text-gray-800">
                        Payment Method
                      </h2>
                    </div>

                    <div className="space-y-4">
                      <div className="space-y-4">
                        <div>
                          <label
                            htmlFor="cardNumberElement"
                            className="block text-sm font-medium text-gray-700 mb-2"
                          >
                            Card Number
                          </label>
                          <div
                            id="cardNumberElement"
                            className="p-3 border border-gray-200 rounded-lg bg-white"
                          >
                            <CardNumberElement
                              options={{
                                style: {
                                  base: {
                                    fontSize: '16px',
                                    color: '#1f2937',
                                    '::placeholder': { color: '#9ca3af' },
                                  },
                                  invalid: { color: '#ef4444' },
                                },
                              }}
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label
                              htmlFor="cardExpiryElement"
                              className="block text-sm font-medium text-gray-700 mb-2"
                            >
                              Expiry Date
                            </label>
                            <div
                              id="cardExpiryElement"
                              className="p-3 border border-gray-200 rounded-lg bg-white"
                            >
                              <CardExpiryElement
                                options={{
                                  style: {
                                    base: {
                                      fontSize: '16px',
                                      color: '#1f2937',
                                      '::placeholder': { color: '#9ca3af' },
                                    },
                                    invalid: { color: '#ef4444' },
                                  },
                                }}
                              />
                            </div>
                          </div>
                          <div>
                            <label
                              htmlFor="cardCvcElement"
                              className="block text-sm font-medium text-gray-700 mb-2"
                            >
                              CVC
                            </label>
                            <div
                              id="cardCvcElement"
                              className="p-3 border border-gray-200 rounded-lg bg-white"
                            >
                              <CardCvcElement
                                options={{
                                  style: {
                                    base: {
                                      fontSize: '16px',
                                      color: '#1f2937',
                                      '::placeholder': { color: '#9ca3af' },
                                    },
                                    invalid: { color: '#ef4444' },
                                  },
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <input
                          id="saveCard"
                          type="checkbox"
                          name="saveCard"
                          checked={formData.saveCard}
                          onChange={handleChange}
                          className="w-4 h-4 text-[#8510DF] border-gray-300 rounded focus:ring-[#8510DF]"
                        />
                        <label
                          htmlFor="saveCard"
                          className="text-sm text-gray-700"
                        >
                          Save this card for future payments and renewals
                        </label>
                      </div>

                      <div className="flex items-center gap-2 text-sm text-green-600">
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M9 12L11 14L15 10"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <span>
                          Payments are securely processed via encrypted
                          gateways.
                        </span>
                      </div>
                    </div>
                  </div>

                  {error && (
                    <div className="p-3 rounded-lg bg-red-50 text-red-700 border border-red-200">
                      {error}
                    </div>
                  )}
                </form>
              </Card>
            </div>

            <div>
              <SetupProgress currentStep={2} />
            </div>
          </div>

          <div className="mt-8 flex justify-between">
            <Button
              variant="link"
              fullWidth={false}
              onClick={() => navigate(ROUTES.SETUP)}
              className="group inline-flex items-center justify-center gap-2 rounded-full border border-gray-200 bg-white/80 !px-4 !py-2 text-xs font-medium text-gray-600 shadow-sm transition-colors hover:border-gray-300 hover:bg-white hover:text-gray-800 sm:!px-5 sm:!py-2.5 sm:text-sm lg:!px-6 lg:!py-3 lg:text-base"
            >
              <span className="inline-flex items-center gap-2">
                <span className="transition-transform duration-200 group-hover:-translate-x-1 group-focus-visible:-translate-x-1">
                  ←
                </span>
                <span>Back</span>
              </span>
            </Button>
            <div className="flex gap-4">
              <Button
                variant="link"
                fullWidth={false}
                onClick={() => navigate(ROUTES.SETUP_PROFILE)}
              >
                Skip
              </Button>
              <Button
                fullWidth={false}
                type="button"
                onClick={() => handleSubmit()}
                className="px-6"
              >
                Save & Continue
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const BillingDetailsScreen = () => (
  <Elements stripe={stripePromise}>
    <BillingForm />
  </Elements>
);

export default BillingDetailsScreen;
